import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Settings, Clock, Building2, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const AdminOverrides = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);

  useEffect(() => {
    loadOverrides();
  }, [typeFilter, includeExpired]);

  const loadOverrides = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append('override_type', typeFilter);
      if (includeExpired) params.append('include_expired', 'true');
      
      const response = await fetch(
        `${API_BASE}/admin/support/overrides?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to load overrides');
      const data = await response.json();
      setOverrides(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getOverrideTypeLabel = (type) => {
    switch (type) {
      case 'plan_override': return 'Plan Override';
      case 'seat_boost': return 'Seat Boost';
      case 'storage_boost': return 'Storage Boost';
      case 'feature_access': return 'Feature Unlock';
      default: return type;
    }
  };

  const getOverrideTypeVariant = (type) => {
    switch (type) {
      case 'plan_override': return 'default';
      case 'seat_boost': return 'secondary';
      case 'storage_boost': return 'outline';
      case 'feature_access': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">All Overrides</h2>
        <p className="text-muted-foreground">Global view of all active overrides across workspaces</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="plan_override">Plan Override</SelectItem>
                <SelectItem value="seat_boost">Seat Boost</SelectItem>
                <SelectItem value="storage_boost">Storage Boost</SelectItem>
                <SelectItem value="feature_access">Feature Unlock</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeExpired"
                checked={includeExpired}
                onChange={(e) => setIncludeExpired(e.target.checked)}
              />
              <label htmlFor="includeExpired" className="text-sm">
                Include expired
              </label>
            </div>
            
            <Button variant="outline" onClick={loadOverrides}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading overrides...</div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Active Overrides ({overrides.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {overrides.length > 0 ? (
              <div className="space-y-3">
                {overrides.map((override) => (
                  <div
                    key={override.id}
                    className={`flex items-center justify-between p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors ${
                      override.is_expired ? 'opacity-50' : ''
                    }`}
                    onClick={() => navigate(`/admin/support/workspaces/${override.workspace_id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Settings className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getOverrideTypeVariant(override.type)}>
                            {getOverrideTypeLabel(override.type)}
                          </Badge>
                          {override.is_expired && (
                            <Badge variant="destructive">Expired</Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium mt-1">{override.value}</p>
                        <p className="text-xs text-muted-foreground">{override.reason}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground flex items-center gap-1 justify-end">
                        <Building2 className="h-3 w-3" />
                        {override.workspace_id.slice(0, 8)}...
                      </p>
                      <p className="text-muted-foreground flex items-center gap-1 justify-end">
                        <User className="h-3 w-3" />
                        {override.created_by_name || 'Admin'}
                      </p>
                      {override.expires_at && (
                        <p className="text-muted-foreground flex items-center gap-1 justify-end">
                          <Clock className="h-3 w-3" />
                          {override.days_remaining !== null
                            ? `${override.days_remaining} days left`
                            : new Date(override.expires_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                No overrides found
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminOverrides;
